// ─────────────────────────────────────────────────────────────────────────────
// GrievanceCreateForm — 4-step grievance intake wizard.
//   1. Grievance Details   2. Follow Up   3. Resolution   4. Associated Files
// Mirrors the reference workflow (incl. "add follow-up notes before resolving").
// On save, emits a record conforming to the shared grievanceModel shape.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect } from "react";
import { Card } from "./reports/_shared";
import {
  METRICS_FACILITIES, CATEGORIES, DEPARTMENTS, STATUSES, COMPLAINANT_TYPES, CLOSED_STATUSES,
} from "./grievanceModel";
import { genGrievanceId } from "./grievanceStore";
import { getDataset } from "./data/reportingAdapter";

const STEPS = [
  { n: 1, label: "Grievance Details" },
  { n: 2, label: "Follow Up" },
  { n: 3, label: "Resolution" },
  { n: 4, label: "Associated Files" },
];

const today = () => new Date().toISOString().slice(0, 10);

export default function GrievanceCreateForm({ defaultFacilityCcn = "ALL", facilities = METRICS_FACILITIES, source, filedBy = "", onCancel, onSave }) {
  const [step, setStep] = useState(1);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({
    facilityCcn: defaultFacilityCcn && defaultFacilityCcn !== "ALL" ? defaultFacilityCcn : "",
    assignedTo: "",
    category: "",
    department: "",
    complainantType: "resident",
    relationToResident: "",
    residentName: "",
    dateReceived: today(),
    incidentDate: "",
    narrative: "",
    investigationNotes: "",
    employeesMentioned: "",
    status: "open",
    resolutionDate: "",
    outcome: "",
    residentNotificationSent: false,
    residentNotificationDate: "",
  });
  const [actionNotes, setActionNotes] = useState([]); // [{ note, addedBy, date }]
  const [noteDraft, setNoteDraft] = useState("");
  const [attachments, setAttachments] = useState([]); // [{ name }]
  const [fileDraft, setFileDraft] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Load real residents for the chosen facility (live PCC feed or mock) to power
  // the Resident autocomplete. Cleared when no specific facility is selected.
  const [residents, setResidents] = useState([]);
  useEffect(() => {
    let cancelled = false;
    const fc = form.facilityCcn;
    if (!fc || fc === "ALL") { setResidents([]); return; }
    getDataset("residents", { facilityCcn: fc, source })
      .then((rows) => { if (!cancelled) setResidents(Array.from(new Set((rows || []).map((r) => r.name).filter(Boolean))).sort()); })
      .catch(() => { if (!cancelled) setResidents([]); });
    return () => { cancelled = true; };
  }, [form.facilityCcn, source]);

  const hasFollowUp = form.investigationNotes.trim() !== "" || actionNotes.length > 0;

  const goto = (n) => {
    setErr("");
    // Step 1 gate: facility + category required.
    if (n > 1 && (!form.facilityCcn || !form.category)) {
      setStep(1);
      setErr("Facility and Grievance Category are required.");
      return;
    }
    // Resolution gate: must log follow-up before resolving/closing (matches reference).
    if (n === 3 && !hasFollowUp) {
      setErr("Add one or more follow-up notes/actions before working the resolution.");
      setStep(2);
      return;
    }
    setStep(n);
  };

  const addNote = () => {
    if (!noteDraft.trim()) return;
    setActionNotes((prev) => [...prev, { note: noteDraft.trim(), addedBy: filedBy || "Current user", date: today() }]);
    setNoteDraft("");
  };
  const addFile = () => {
    if (!fileDraft.trim()) return;
    setAttachments((prev) => [...prev, { name: fileDraft.trim() }]);
    setFileDraft("");
  };

  const submit = () => {
    setErr("");
    if (!form.facilityCcn || !form.category) { setStep(1); setErr("Facility and Grievance Category are required."); return; }
    const resolving = CLOSED_STATUSES.includes(form.status);
    if (resolving && !hasFollowUp) { setStep(2); setErr("Add follow-up notes/actions before resolving."); return; }

    const facility = METRICS_FACILITIES.find((f) => f.ccn === form.facilityCcn);
    const record = {
      id: genGrievanceId(),
      facilityCcn: form.facilityCcn,
      facilityName: facility ? facility.name : form.facilityCcn,
      complainantType: form.complainantType,
      residentName: form.residentName.trim() || null,
      dateReceived: form.dateReceived || today(),
      category: form.category,
      department: form.department || "administration",
      assignedTo: form.assignedTo.trim() || null,
      status: form.status,
      investigationNotes: form.investigationNotes.trim(),
      resolutionDate: resolving ? (form.resolutionDate || today()) : null,
      outcome: form.outcome.trim() || null,
      residentNotificationSent: resolving ? !!form.residentNotificationSent : null,
      residentNotificationDate: resolving && form.residentNotificationSent ? (form.residentNotificationDate || today()) : null,
      employeesMentioned: form.employeesMentioned.split(",").map((s) => s.trim()).filter(Boolean),
      narrative: form.narrative.trim(),
      // capture-only extras (reports ignore these; the detail view surfaces them)
      incidentDate: form.incidentDate || null,
      relationToResident: form.relationToResident.trim() || null,
      filedBy: filedBy || null,
      filedDate: today(),
      actionNotes,
      attachments,
      createdLocally: true,
    };
    onSave && onSave(record);
  };

  return (
    <div style={{ maxWidth: 880, margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14, flexWrap: "wrap", gap: 8 }}>
        <h2 style={{ fontSize: 20, fontWeight: 800, color: "var(--ed-ink)", margin: 0 }}>New Grievance</h2>
        <span className="badge" style={{ color: "var(--ed-amber-deep)", background: "var(--ed-amber-soft)", border: "1px solid var(--ed-amber-line)" }}>Draft</span>
      </div>

      {/* Stepper */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        {STEPS.map((s) => {
          const active = step === s.n;
          const done = step > s.n;
          return (
            <button key={s.n} onClick={() => goto(s.n)}
              style={{ display: "flex", alignItems: "center", gap: 8, background: "none", border: "none", cursor: "pointer", padding: "4px 6px", fontFamily: "inherit" }}>
              <span style={{ width: 24, height: 24, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700,
                background: active ? "var(--ed-brand)" : done ? "var(--ed-brand-lift)" : "var(--ed-line)", color: active || done ? "white" : "var(--ed-muted)" }}>{done ? "✓" : s.n}</span>
              <span style={{ fontSize: 13.5, fontWeight: active ? 700 : 500, color: active ? "var(--ed-brand)" : "var(--ed-muted)" }}>{s.label}</span>
            </button>
          );
        })}
      </div>

      {err && (
        <div style={{ background: "var(--ed-alert-soft)", border: "1px solid var(--ed-alert-line)", color: "var(--ed-alert-deep)", borderRadius: 8, padding: "10px 14px", fontSize: 13, marginBottom: 14 }}>{err}</div>
      )}

      <Card style={{ padding: 22 }}>
        {step === 1 && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 16 }}>
            <Field label="Facility *">
              <select value={form.facilityCcn} onChange={(e) => set("facilityCcn", e.target.value)} style={inp}>
                <option value="">Select facility…</option>
                {facilities.map((f) => <option key={f.ccn} value={f.ccn}>{f.pcc ? "🔌 " : ""}{f.name}{f.pcc ? "" : ` · ${f.ccn}`}</option>)}
              </select>
            </Field>
            <Field label="Grievance Category *">
              <select value={form.category} onChange={(e) => set("category", e.target.value)} style={inp}>
                <option value="">Select category…</option>
                {Object.entries(CATEGORIES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </Field>
            <Field label="Assigned To">
              <input value={form.assignedTo} onChange={(e) => set("assignedTo", e.target.value)} placeholder="Grievance official" style={inp} />
            </Field>
            <Field label="Department">
              <select value={form.department} onChange={(e) => set("department", e.target.value)} style={inp}>
                <option value="">Select department…</option>
                {Object.entries(DEPARTMENTS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </Field>
            <Field label="Reported Date">
              <input type="date" value={form.dateReceived} onChange={(e) => set("dateReceived", e.target.value)} style={inp} />
            </Field>
            <Field label="Incident Date">
              <input type="date" value={form.incidentDate} onChange={(e) => set("incidentDate", e.target.value)} style={inp} />
            </Field>
            <Field label="Reported By">
              <select value={form.complainantType} onChange={(e) => set("complainantType", e.target.value)} style={inp}>
                {Object.entries(COMPLAINANT_TYPES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </Field>
            <Field label="Relation to Resident">
              <input value={form.relationToResident} onChange={(e) => set("relationToResident", e.target.value)} placeholder="e.g. daughter, POA" style={inp} />
            </Field>
            <Field label="Resident">
              <ResidentAutocomplete value={form.residentName} onChange={(v) => set("residentName", v)} options={residents} />
              {form.facilityCcn && form.facilityCcn !== "ALL" && (
                <div style={{ fontSize: 11, color: "var(--ed-muted-2)", marginTop: 4 }}>
                  {residents.length ? `${residents.length} resident${residents.length === 1 ? "" : "s"} from feed — type to search` : "No residents from feed for this facility"}
                </div>
              )}
            </Field>
            <Field label="Employees Mentioned">
              <input value={form.employeesMentioned} onChange={(e) => set("employeesMentioned", e.target.value)} placeholder="Comma-separated names" style={inp} />
            </Field>
            <div style={{ gridColumn: "1 / -1" }}>
              <Field label="Description / Narrative">
                <textarea value={form.narrative} onChange={(e) => set("narrative", e.target.value)} rows={4} placeholder="What happened, in the complainant's words…" style={{ ...inp, resize: "vertical" }} />
              </Field>
            </div>
          </div>
        )}

        {step === 2 && (
          <div>
            <Field label="Investigation Notes">
              <textarea value={form.investigationNotes} onChange={(e) => set("investigationNotes", e.target.value)} rows={4} placeholder="Findings from the investigation…" style={{ ...inp, resize: "vertical" }} />
            </Field>
            <div style={{ marginTop: 18 }}>
              <div className="section-header">Follow-Up Actions</div>
              {actionNotes.length === 0 && <div style={{ color: "var(--ed-muted-2)", fontSize: 13, marginBottom: 10 }}>No action notes yet.</div>}
              {actionNotes.map((a, i) => (
                <div key={i} style={{ display: "flex", gap: 10, fontSize: 13, padding: "8px 0", borderBottom: "1px solid var(--ed-line-2)" }}>
                  <span style={{ color: "var(--ed-muted-2)", whiteSpace: "nowrap" }}>{a.date}</span>
                  <span style={{ color: "var(--ed-muted-2)", whiteSpace: "nowrap" }}>{a.addedBy}</span>
                  <span style={{ color: "var(--ed-ink-2)" }}>{a.note}</span>
                </div>
              ))}
              <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                <input value={noteDraft} onChange={(e) => setNoteDraft(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") addNote(); }} placeholder="Add a follow-up action…" style={{ ...inp, flex: 1 }} />
                <button className="btn-primary" onClick={addNote} style={{ whiteSpace: "nowrap" }}>＋ Add</button>
              </div>
            </div>
          </div>
        )}

        {step === 3 && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 16 }}>
            <Field label="Status">
              <select value={form.status} onChange={(e) => set("status", e.target.value)} style={inp}>
                {Object.entries(STATUSES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </Field>
            <Field label="Resolution Date">
              <input type="date" value={form.resolutionDate} onChange={(e) => set("resolutionDate", e.target.value)} style={inp} disabled={!CLOSED_STATUSES.includes(form.status)} />
            </Field>
            <div style={{ gridColumn: "1 / -1" }}>
              <Field label="Outcome">
                <textarea value={form.outcome} onChange={(e) => set("outcome", e.target.value)} rows={3} placeholder="How the grievance was resolved…" style={{ ...inp, resize: "vertical" }} />
              </Field>
            </div>
            <div style={{ gridColumn: "1 / -1", display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", background: "var(--ed-bg)", borderRadius: 8, border: "1px solid var(--ed-line)" }}>
              <input id="notif" type="checkbox" checked={form.residentNotificationSent} onChange={(e) => set("residentNotificationSent", e.target.checked)} style={{ width: 18, height: 18 }} />
              <label htmlFor="notif" style={{ fontSize: 13.5, color: "var(--ed-ink-2)", fontWeight: 600 }}>Resident / representative notified of the resolution (F585)</label>
              {form.residentNotificationSent && (
                <input type="date" value={form.residentNotificationDate} onChange={(e) => set("residentNotificationDate", e.target.value)} style={{ ...inp, width: "auto", marginLeft: "auto" }} />
              )}
            </div>
            {CLOSED_STATUSES.includes(form.status) && !form.residentNotificationSent && (
              <div style={{ gridColumn: "1 / -1", fontSize: 12.5, color: "var(--ed-amber-deep)" }}>⚠ Resolving without resident notification will show up as a Notification Gap on the dashboard.</div>
            )}
          </div>
        )}

        {step === 4 && (
          <div>
            <div className="section-header">Associated Files</div>
            <div style={{ fontSize: 12.5, color: "var(--ed-muted-2)", marginBottom: 10 }}>Reference attachments by name (demo — no upload in v1).</div>
            {attachments.length === 0 && <div style={{ color: "var(--ed-muted-2)", fontSize: 13, marginBottom: 10 }}>No files attached.</div>}
            {attachments.map((a, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, padding: "7px 0", borderBottom: "1px solid var(--ed-line-2)" }}>
                <span>📎</span><span style={{ color: "var(--ed-ink-2)" }}>{a.name}</span>
                <button className="btn-ghost" onClick={() => setAttachments((p) => p.filter((_, j) => j !== i))} style={{ marginLeft: "auto", fontSize: 12 }}>Remove</button>
              </div>
            ))}
            <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
              <input value={fileDraft} onChange={(e) => setFileDraft(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") addFile(); }} placeholder="e.g. incident-report.pdf" style={{ ...inp, flex: 1 }} />
              <button className="btn-primary" onClick={addFile} style={{ whiteSpace: "nowrap" }}>＋ Add</button>
            </div>
          </div>
        )}
      </Card>

      {/* Footer actions */}
      <div style={{ display: "flex", gap: 10, justifyContent: "space-between", marginTop: 16, flexWrap: "wrap" }}>
        <button className="btn-secondary" onClick={onCancel}>Cancel</button>
        <div style={{ display: "flex", gap: 10 }}>
          {step > 1 && <button className="btn-secondary" onClick={() => goto(step - 1)}>← Back</button>}
          {step < 4 && <button className="btn-primary" onClick={() => goto(step + 1)}>Next →</button>}
          <button className="btn-primary" onClick={submit} style={{ background: "var(--ed-success)" }}>Save Grievance</button>
        </div>
      </div>
    </div>
  );
}

const inp = { width: "100%", border: "1px solid var(--ed-line-hover)", borderRadius: 7, padding: "9px 12px", fontSize: 14, color: "#1e293b", background: "#fafbfc", outline: "none" };

function Field({ label, children }) {
  return (
    <div>
      <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--ed-ink-3)", textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 6 }}>{label}</label>
      {children}
    </div>
  );
}

// Type-ahead resident picker: filters `options` (resident names) by the typed
// text and shows a clickable dropdown. Still allows free text (anonymous /
// not-listed). On focus with no text, shows the full list.
function ResidentAutocomplete({ value, onChange, options = [] }) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const q = (value || "").trim().toLowerCase();
  const matches = (q ? options.filter((n) => n.toLowerCase().includes(q)) : options).slice(0, 50);

  const choose = (n) => { onChange(n); setOpen(false); setActive(-1); };

  return (
    <div style={{ position: "relative" }}>
      <input
        value={value}
        autoComplete="off"
        placeholder={options.length ? "Type to search residents…" : "Resident name (if applicable)"}
        onChange={(e) => { onChange(e.target.value); setOpen(true); setActive(-1); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onKeyDown={(e) => {
          if (!open && (e.key === "ArrowDown" || e.key === "ArrowUp")) { setOpen(true); return; }
          if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, matches.length - 1)); }
          else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
          else if (e.key === "Enter" && active >= 0 && matches[active]) { e.preventDefault(); choose(matches[active]); }
          else if (e.key === "Escape") { setOpen(false); }
        }}
        style={inp}
      />
      {open && matches.length > 0 && (
        <div style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 50, background: "var(--ed-canvas)", border: "1px solid var(--ed-line-hover)", borderRadius: 8, marginTop: 4, maxHeight: 220, overflowY: "auto", boxShadow: "0 8px 24px rgba(15,23,42,.14)" }}>
          {matches.map((n, i) => (
            <div
              key={n}
              // onMouseDown (not onClick) so selection fires before the input's onBlur closes the list
              onMouseDown={(e) => { e.preventDefault(); choose(n); }}
              onMouseEnter={() => setActive(i)}
              style={{ padding: "8px 12px", fontSize: 13.5, cursor: "pointer", color: "var(--ed-ink-2)", background: i === active ? "var(--ed-brand-soft)" : "white" }}
            >
              {n}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
